<script lang="ts">
    import Vue from "vue";
    import Component from 'vue-class-component';
    import { strCompareLowerCase } from '@common/Common';
    import { AlternativeReadModel } from '@Alternatives/Alternatives/Domain/readmodels';
    import { AlternativeRelationshipsDialogViewModel } from '@Alternatives/Alternatives/Components/AlternativeRelationshipsDialog';

    @Component({
        props: {
            viewModel: {
                type: Object,
                required: true
            }
        }
    })
    export default class AlternativeRelationshipsDialog extends Vue
    {
        get vm(): AlternativeRelationshipsDialogViewModel { return this.$props.viewModel as AlternativeRelationshipsDialogViewModel; }
        get opened(): boolean { return this.vm?.opened ?? false; }

        get workspaceId(): number { return this.vm.workspaceId; }
        get tchProjectId(): number { return this.vm.tchProjectId; }
        get alternativeLabel(): string { return this.vm.alternativeLabel; }
        get alternativesLabel(): string { return this.vm.alternativesLabel; }

        get alternativeId(): string { return this.vm.alternativeId; }
        get alternativeName(): string { return this.vm.alternativeName; }
        get alternativeStatusId(): number { return this.vm.alternativeStatusId; }
        get alternativeStatusName(): string { return this.vm.alternativeStatusName; }       

        get dependents(): AlternativeReadModel[] { return this.vm.dependents; }
        get foundations(): AlternativeReadModel[] { return this.vm.foundations; }

        searchOtherPhrase: string | null = null;
        tab: string = "Foundations";

        get dependentsCandidatesView(): AlternativeReadModel[] 
        {
            const currIds = this.dependents.map(a => a.Id);
            currIds.push(this.alternativeId);

            let view = this.vm.allAlternatives?.filter(a => currIds.indexOf(a.Id) === -1) ?? [];

            if (this.searchOtherPhrase)
            {
                view = view.filter(a => (a.Name && a.Name.toLowerCase().indexOf(this.searchOtherPhrase.toLowerCase()) !== -1));
            }

            return view.sort((a, b) => strCompareLowerCase(a.Name, b.Name));
        }

        get foundationsCandidatesView(): AlternativeReadModel[] 
        {
            const currIds = this.foundations.map(a => a.Id);
            currIds.push(this.alternativeId);

            let view = this.vm.allAlternatives?.filter(a => currIds.indexOf(a.Id) === -1) ?? [];

            if (this.searchOtherPhrase)
            {
                view = view.filter(a => (a.Name && a.Name.toLowerCase().indexOf(this.searchOtherPhrase.toLowerCase()) !== -1));
            }

            return view.sort((a, b) => strCompareLowerCase(a.Name, b.Name));
        }

        loadAlternatives()
        {
           if (this.vm.allAlternatives == null)
           {
               this.$emit('load-alternatives');
           }
        }

        
        handleUnlinkDependent(d: AlternativeReadModel)
        {
            this.$emit('unlink-dependent', d.Id);
        }

        handleUnlinkFoundation(f: AlternativeReadModel)
        {
            this.$emit('unlink-foundation', f.Id);
        }

        handleLinkFoundation(f: AlternativeReadModel)
        {
            this.$emit('link-foundation', f.Id);
        }

        handleLinkDependent(d: AlternativeReadModel)
        {
            this.$emit('link-dependent', d.Id);
        }

        close()
        {
            this.vm.close();
        }
    }

</script>

<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-lg modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Dependencies</h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <p>{{alternativeLabel}}: <a target="_blank" v-sanitizedHref="`/#/alternative/dashboard/${workspaceId}/${tchProjectId}/${alternativeId}`">{{alternativeName}}</a></p>
                        <p>Status: {{alternativeStatusName}}</p>
                        <br />

                        <ul class="nav nav-tabs" role="tablist">
                            <li class="nav-item">
                                <a class="nav-link" @click="tab = 'Foundations'" :class="{ active: tab === 'Foundations' }">
                                    Foundations ({{foundations.length}})
                                </a>
                            </li>
                            <li class="nav-item">
                                <a class="nav-link" @click="tab = 'Dependents'" :class="{ active: tab === 'Dependents' }">
                                    Dependents ({{dependents.length}})
                                </a>
                            </li>
                        </ul>
                        <br />

                        <div class="tab-content" style="height: 400px;">
                            <div class="tab-pane" :class="{ active: tab === 'Foundations' }">
                                <div>
                                    This {{alternativeLabel.toLowerCase()}} is dependent on {{foundations.length}} other {{alternativesLabel.toLowerCase()}}

                                    <div class="dropdown d-inline-block">
                                        <a class="text-primary" data-toggle="dropdown" @click="loadAlternatives">Add a foundation</a>
                                        <div class="dropdown-menu">
                                            <form class="px-2 py-1">
                                                <div class="form-group">
                                                    <input type="text" class="form-control" placeholder="Search..." v-model="searchOtherPhrase" />
                                                </div>
                                            </form>
                                            <div style="max-height: 200px; overflow-y: auto;">
                                                <a class="dropdown-item" v-for="a in foundationsCandidatesView" :key="a.Id" @click="handleLinkFoundation(a)">
                                                    {{a.Name}}
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <table class="table table-hover mt-3" v-if="foundations.length > 0">
                                    <thead>
                                        <tr>
                                            <th>{{alternativeLabel}}</th>
                                            <th>Status</th>
                                            <th style="width: 100px;"></th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        <tr v-for="f in foundations" :key="f.Id" :class="{'table-warning': (f.StatusId !== alternativeStatusId) }">
                                            <td class="align-middle">
                                                <a target="_blank" v-sanitizedHref="`/#/alternative/dashboard/${workspaceId}/${tchProjectId}/${f.Id}`">{{f.Name}}</a>
                                            </td>
                                            <td class="align-middle">{{f.StatusName}}</td>
                                            <td class="align-middle">
                                                <button class="btn btn-sm btn-outline-secondary" @click="handleUnlinkFoundation(f)" title="Remove dependency">
                                                    <i class="fa fa-chain-broken" aria-hidden="true"></i>
                                                </button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div class="tab-pane" :class="{ active: tab === 'Dependents' }">
                                <div>
                                    <span>
                                        This {{alternativeLabel.toLowerCase()}} is a foundation for {{dependents.length}} other {{alternativesLabel.toLowerCase()}}.
                                    </span>
                                    
                                    <div class="dropdown d-inline-block">
                                        <a class="text-primary" data-toggle="dropdown" @click="loadAlternatives">Add a dependent</a>
                                        <div class="dropdown-menu">
                                            <form class="px-2 py-1">
                                                <div class="form-group">
                                                    <input type="text" class="form-control" placeholder="Search..." v-model="searchOtherPhrase" />
                                                </div>
                                            </form>
                                            <div style="max-height: 200px; overflow-y: auto;">
                                                <a class="dropdown-item" v-for="a in dependentsCandidatesView" :key="a.Id" @click="handleLinkDependent(a)">
                                                    {{a.Name}}
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <table class="table table-hover mt-3" v-if="dependents.length > 0">
                                    <thead>
                                        <tr>
                                            <th>{{alternativeLabel}}</th>
                                            <th>Status</th>
                                            <th style="width: 100px;"></th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        <tr v-for="d in dependents" :key="d.Id" :class="{'table-warning': (d.StatusId !== alternativeStatusId) }">
                                            <td class="align-middle">
                                                <a target="_blank" v-sanitizedHref="`/#/alternative/dashboard/${workspaceId}/${tchProjectId}/${d.Id}`">{{d.Name}}</a>
                                            </td>
                                            <td class="align-middle">{{d.StatusName}}</td>
                                            <td>
                                                <button class="btn btn-sm btn-outline-secondary" @click="handleUnlinkDependent(d)" title="Remove dependency">
                                                    <i class="fa fa-chain-broken" aria-hidden="true"></i>
                                                </button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                    </div>

                    <div class="modal-footer">
                        <button @click="close" type="button" class="btn btn-outline-secondary">Close</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>